/** @jsxImportSource react */
import { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Icon,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BusinessFormData, BusinessCredentials } from "./types";
import { steps, defaultValues } from "./constants";
import { StyledButton } from "./StyledComponents";
import { BusinessInformationStep } from "./BusinessInformationStep";
import { SubscriptionStep } from "./SubscriptionStep";
import { SettingsStep } from "./SettingsStep";

function generateSecurePassword() {
  const length = 12;
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}

interface BusinessFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    data: BusinessFormData & { owner: { password: string } }
  ) => Promise<void>;
  initialData?: Partial<BusinessFormData>;
}

const validationSchema = Yup.object({
  business_name: Yup.string().required("Business name is required"),
  owner: Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
  }),
  subscription: Yup.object({
    plan: Yup.string().required("Plan is required"),
    features: Yup.object({
      max_users: Yup.number().min(1, "Must be at least 1").required("Required"),
      max_products: Yup.number()
        .min(1, "Must be at least 1")
        .required("Required"),
      max_locations: Yup.number()
        .min(1, "Must be at least 1")
        .required("Required"),
      has_inventory: Yup.boolean(),
      has_analytics: Yup.boolean(),
      has_customer_loyalty: Yup.boolean(),
    }),
  }),
  settings: Yup.object({
    timezone: Yup.string().required("Timezone is required"),
    language: Yup.string().required("Language is required"),
    currency: Yup.string().required("Currency is required"),
    tax_settings: Yup.object({
      tax_included: Yup.boolean(),
      default_tax_rate: Yup.number()
        .min(0, "Must be at least 0")
        .max(100, "Must be at most 100")
        .required("Required"),
    }),
  }),
});

export const BusinessForm: React.FC<BusinessFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<BusinessCredentials | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik<BusinessFormData>({
    initialValues: initialData
      ? { ...defaultValues, ...initialData }
      : defaultValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const generatedPassword = generateSecurePassword();
        const submissionData = {
          ...values,
          owner: {
            ...values.owner,
            password: generatedPassword,
          },
        };

        // Wait for the API response
        await onSubmit(submissionData);

        // Only set credentials after successful API response
        setCredentials({
          email: values.owner.email,
          password: generatedPassword,
          business_name: values.business_name,
        });
      } catch (error) {
        console.error("Failed to create business:", error);
        toast.error("Failed to create business. Please try again.");
        setCredentials(null); // Reset credentials on error
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const touchFields = useCallback(() => {
    switch (activeStep) {
      case 0:
        formik.setFieldTouched("business_name", true);
        formik.setFieldTouched("owner.email", true);
        formik.setFieldTouched("owner.first_name", true);
        formik.setFieldTouched("owner.last_name", true);
        break;
      case 1:
        formik.setFieldTouched("subscription.plan", true);
        formik.setFieldTouched("subscription.features.max_users", true);
        formik.setFieldTouched("subscription.features.max_products", true);
        formik.setFieldTouched("subscription.features.max_locations", true);
        break;
      case 2:
        formik.setFieldTouched("settings.timezone", true);
        formik.setFieldTouched("settings.language", true);
        formik.setFieldTouched("settings.currency", true);
        break;
    }
  }, [activeStep, formik]);

  const isStepValid = useCallback(() => {
    let isValid = true;

    switch (activeStep) {
      case 0:
        isValid = !(
          formik.errors.business_name ||
          formik.errors.owner?.email ||
          formik.errors.owner?.first_name ||
          formik.errors.owner?.last_name
        );
        break;
      case 1:
        isValid = !(
          formik.errors.subscription?.plan ||
          formik.errors.subscription?.features?.max_users ||
          formik.errors.subscription?.features?.max_products ||
          formik.errors.subscription?.features?.max_locations
        );
        break;
      case 2:
        isValid = !(
          formik.errors.settings?.timezone ||
          formik.errors.settings?.language ||
          formik.errors.settings?.currency
        );
        break;
    }

    return isValid;
  }, [activeStep, formik.errors]);

  const handleNext = () => {
    touchFields();
    if (isStepValid()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCancel = () => {
    formik.resetForm();
    setActiveStep(0);
    onClose();
  };

  const handleCopyCredentials = () => {
    if (credentials) {
      const text = `Business Name: ${credentials.business_name}
Email: ${credentials.email}
Password: ${credentials.password}`;
      navigator.clipboard.writeText(text);
    }
  };

  const handleSuccessClose = () => {
    setCredentials(null);
    onClose();
    navigate("/super/businesses");
  };

  const handleStepClick = (step: number) => {
    if (step < activeStep) {
      setActiveStep(step);
    } else if (step > activeStep) {
      // Check if all previous steps are valid before allowing to jump forward
      let canProceed = true;
      for (let i = activeStep; i < step; i++) {
        touchFields();
        if (!isStepValid()) {
          canProceed = false;
          break;
        }
      }
      if (canProceed) {
        setActiveStep(step);
      }
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <BusinessInformationStep formik={formik} />;
      case 1:
        return <SubscriptionStep formik={formik} />;
      case 2:
        return <SettingsStep formik={formik} />;
      default:
        return null;
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Prevent form submission unless explicitly triggered by the Create Business button
    e.preventDefault();

    if (activeStep === steps.length - 1) {
      formik.handleSubmit(e);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon>{steps[activeStep].icon}</Icon>
              <Typography variant="h6">{steps[activeStep].label}</Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {activeStep === 0 &&
                  "Enter the basic information for the business and the owner's email."}
                {activeStep === 1 &&
                  "Configure the subscription plan and set limits for users, products, and locations."}
                {activeStep === 2 &&
                  "Set up business preferences including timezone, language, and tax settings."}
              </Typography>
            </Box>
            <Stepper activeStep={activeStep} sx={{ py: 3 }}>
              {steps.map((step, index) => (
                <Step
                  key={step.label}
                  sx={{
                    cursor: index <= activeStep ? "pointer" : "not-allowed",
                    "&:hover":
                      index <= activeStep
                        ? {
                            "& .MuiStepLabel-label": {
                              color: "primary.main",
                            },
                          }
                        : {},
                  }}
                  onClick={() => handleStepClick(index)}
                >
                  <StepLabel
                    StepIconComponent={() => (
                      <Icon
                        color={activeStep >= index ? "primary" : "disabled"}
                      >
                        {step.icon}
                      </Icon>
                    )}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Paper variant="outlined" sx={{ mt: 2 }}>
              {renderStepContent()}
            </Paper>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <StyledButton
              onClick={handleCancel}
              variant="text"
              startIcon={<Icon>close</Icon>}
              disabled={isSubmitting}
            >
              Cancel
            </StyledButton>
            <Box sx={{ flex: 1 }} />
            {activeStep > 0 && (
              <StyledButton
                onClick={handleBack}
                variant="outlined"
                startIcon={<Icon>arrow_back</Icon>}
                disabled={isSubmitting}
              >
                Back
              </StyledButton>
            )}
            {activeStep < steps.length - 1 ? (
              <StyledButton
                onClick={handleNext}
                variant="contained"
                type="button"
                disabled={!isStepValid() || isSubmitting}
                endIcon={<Icon>arrow_forward</Icon>}
              >
                Next
              </StyledButton>
            ) : (
              <StyledButton
                onClick={() => formik.handleSubmit()}
                type="button"
                variant="contained"
                disabled={!isStepValid() || isSubmitting}
                endIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Icon>check</Icon>
                  )
                }
              >
                {isSubmitting ? "Creating..." : "Create Business"}
              </StyledButton>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={!!credentials}
        onClose={handleSuccessClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon color="success">check_circle</Icon>
            <Typography variant="h6">Business Created Successfully</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              The business account has been created successfully. Here are the
              login credentials:
            </Typography>
            <Paper sx={{ p: 2, mt: 2, bgcolor: "background.default" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2">
                  <strong>Business Name:</strong> {credentials?.business_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {credentials?.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Password:</strong> {credentials?.password}
                </Typography>
              </Box>
            </Paper>
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              Please make sure to copy these credentials and share them securely
              with the business owner.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton
            onClick={handleCopyCredentials}
            startIcon={<Icon>content_copy</Icon>}
            variant="outlined"
          >
            Copy Credentials
          </StyledButton>
          <StyledButton
            onClick={handleSuccessClose}
            variant="contained"
            endIcon={<Icon>check</Icon>}
          >
            Done
          </StyledButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
